import React, { useEffect, useState } from "react";
import { Box, Button, Skeleton, Typography } from "@mui/material";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import freeVoteIcon from "../../../assets/customerAssets/icons/crownVoteIcon.svg";
import freeVoteIconV from "../../../assets/customerAssets/icons/crownVoteIconRed.svg";
import {
  voteCompetitionVideo,
  voteCompetitionVideoGuest,
} from "../../../api/addCompetitionVote";
import { isPurchaseRequiredMessage } from "../../../utils/competitionVotePurchase";
import GuestUserRistModal from "../../Modals/user/guestUserModalRist/Modal";
import { handleGuestVoteFailure } from "../../../utils/guestVote";

/**
 * Pageant video vote control — same free-vote / guest wallet as Home.
 * onVoted(videoId, nextCount) lets parent update lists + leaderboard.
 */
export default function CompetitionVoteButton({
  videoId,
  count = 0,
  disabled = false,
  onVoted,
  compact = false,
  lightSurface = false,
}) {
  const [clicked, setClicked] = useState(false);
  const [loading, setLoading] = useState(false);
  const [gcount, setCount] = useState(count);
  const [guestModal, setGuestModal] = useState(false);
  const dispatch = useDispatch();
  const user = useSelector((state) => state.user);
  const token = useSelector((state) => state.auth.token);
  const loggedUser = useSelector((state) => state.loggedUser.user);

  useEffect(() => {
    setCount(count || 0);
  }, [count]);

  const handleClicked = async (e) => {
    e?.preventDefault?.();
    e?.stopPropagation?.();
    if (disabled) {
      toast.info("Voting opens when this competition is Live.");
      return;
    }
    try {
      setLoading(true);
      let data;
      if (loggedUser?.device_id) {
        data = await voteCompetitionVideoGuest(
          videoId,
          loggedUser.id,
          token
        );
      } else if (token && user?.data?.id) {
        data = await voteCompetitionVideo(videoId, token);
      } else {
        toast.error("Please log in to vote");
        return;
      }

      if (data?.success) {
        const next =
          typeof data.voteCount === "number" ? data.voteCount : gcount + 1;
        setCount(next);
        setClicked(true);
        if (!loggedUser?.device_id) {
          dispatch({ type: "DCREMENT_VOTE" });
        }
        onVoted?.(videoId, next, {
          remainingVotes: data.remainingVotes,
          dailyVotesUsedToday: data.dailyVotesUsedToday,
        });
        toast.success(data.message || "Vote cast!");
      } else {
        handleGuestVoteFailure(data, {
          onLimit: () => setGuestModal(true),
          onDisabled: (msg) => toast.error(msg || data?.message),
        });
        toast.error(data?.message || "Could not vote");
        if (
          data?.requiresPurchase ||
          isPurchaseRequiredMessage(data?.message)
        ) {
          onVoted?.(videoId, gcount, {
            remainingVotes: data.remainingVotes ?? 0,
            dailyVotesUsedToday: data.dailyVotesUsedToday,
            requiresPurchase: true,
          });
        }
      }
    } catch (error) {
      if (error?.guestVotingDisabled) {
        toast.error(error.message);
      } else if (error?.requiresRegistration) {
        setGuestModal(true);
      }
      toast.error(error.message);
      if (isPurchaseRequiredMessage(error.message)) {
        onVoted?.(videoId, gcount, {
          requiresPurchase: true,
        });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
    <GuestUserRistModal open={guestModal} setOpen={setGuestModal} />
    <Box
      onClick={(e) => {
        e.preventDefault();
        e.stopPropagation();
      }}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: compact ? 0.5 : 0.75,
        px: compact ? 1 : 1.25,
        py: compact ? 0.4 : 0.55,
        borderRadius: 999,
        bgcolor: lightSurface
          ? "rgba(128, 113, 84, 0.1)"
          : "rgba(239,54,78,0.12)",
        border: lightSurface
          ? "1px solid rgba(128, 113, 84, 0.35)"
          : "1px solid rgba(239,54,78,0.35)",
        backdropFilter: lightSurface ? "none" : "blur(6px)",
        transition: "transform 0.2s ease, box-shadow 0.2s ease",
        cursor: disabled ? "not-allowed" : "pointer",
        "&:hover": disabled
          ? undefined
          : {
              transform: "scale(1.04)",
              boxShadow: "0 0 18px rgba(239,54,78,0.35)",
            },
        opacity: disabled ? 0.55 : 1,
      }}
    >
      <Button
        disabled={loading}
        onClick={handleClicked}
        sx={{
          minWidth: 0,
          p: 0,
          "& img": { width: compact ? 22 : 26, height: compact ? 22 : 26 },
        }}
      >
        {loading ? (
          <Skeleton animation="wave" variant="circular" width={26} height={26} />
        ) : (
          <img
            src={clicked ? freeVoteIconV : freeVoteIcon}
            alt="Vote"
            className="iconImg"
          />
        )}
      </Button>
      <Typography
        sx={{
          color: lightSurface ? "#222222" : "#fff",
          fontWeight: 800,
          fontFamily: "Montserrat, sans-serif",
          fontSize: compact ? 13 : 14,
          minWidth: 18,
          textAlign: "center",
        }}
      >
        {gcount}
      </Typography>
    </Box>
    </>
  );
}
